import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import {
  getClientAccounts, createClientAccount, updateClientAccount,
  getClientNotificationSettings, updateClientNotificationSettings, getClientNotificationLogs,
  getOrganizations,
} from '../../services/api';
import { UserCircle, Plus, EnvelopeSimple, FileText, CurrencyDollar } from '@phosphor-icons/react';
import { toast } from 'sonner';

export function ClientPortalSettings() {
  const [clientAccounts, setClientAccounts] = useState([]);
  const [clientDialog, setClientDialog] = useState(false);
  const [clientForm, setClientForm] = useState({ email: '', password: '', name: '', organization_id: '' });
  const [clientNotifSettings, setClientNotifSettings] = useState(null);
  const [clientNotifLogs, setClientNotifLogs] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => { fetchClientAccounts(); fetchClientNotifSettings(); fetchOrgs(); }, []);

  const fetchOrgs = async () => { try { const res = await getOrganizations(); setOrganizations(res.data.items || []); } catch {} };
  const fetchClientAccounts = async () => { try { const res = await getClientAccounts(); setClientAccounts(res.data.items || []); } catch {} };
  const fetchClientNotifSettings = async () => {
    try {
      const [settingsRes, logsRes] = await Promise.all([getClientNotificationSettings(), getClientNotificationLogs(10)]);
      setClientNotifSettings(settingsRes.data);
      setClientNotifLogs(logsRes.data.items || []);
    } catch {}
  };

  const handleCreateClientAccount = async () => {
    if (!clientForm.email || !clientForm.password || !clientForm.organization_id) { toast.error('Email, mot de passe et organisation requis'); return; }
    try { await createClientAccount(clientForm); toast.success('Compte client cree'); setClientDialog(false); setClientForm({ email: '', password: '', name: '', organization_id: '' }); fetchClientAccounts(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleToggleClientAccount = async (account) => {
    try { await updateClientAccount(account.id, { is_active: !account.is_active }); toast.success(account.is_active ? 'Compte desactive' : 'Compte active'); fetchClientAccounts(); }
    catch { toast.error('Erreur'); }
  };

  const handleToggleClientNotif = async (key, value) => {
    try { await updateClientNotificationSettings({ [key]: value }); setClientNotifSettings(prev => ({ ...prev, [key]: value })); toast.success('Parametre mis a jour'); }
    catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-6">
      {/* Client Accounts */}
      <Card className="border-[#E8E6E1]" data-testid="client-accounts-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}><UserCircle size={18} className="text-[#00AEEF]" /> Comptes Espace Client</CardTitle>
            <Button size="sm" onClick={() => setClientDialog(true)} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="create-client-account-btn"><Plus size={12} className="mr-1" /> Nouveau compte</Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-[#5E6D66] mb-3">Gerez les comptes des organisations pour l'espace client en libre-service (<a href="/espace-client/login" target="_blank" rel="noreferrer" className="text-[#00AEEF] underline">/espace-client</a>)</p>
          {clientAccounts.length === 0 ? (
            <p className="text-sm text-[#5E6D66] text-center py-4">Aucun compte client cree</p>
          ) : (
            <div className="space-y-2" data-testid="client-accounts-list">
              {clientAccounts.map((acc) => {
                const org = organizations.find(o => o.id === acc.organization_id);
                return (
                  <div key={acc.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F9F8F5]" data-testid={`client-account-${acc.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${acc.is_active ? 'bg-emerald-500' : 'bg-red-400'}`} />
                      <div><p className="text-xs font-medium text-[#1A2421]">{acc.email}</p><p className="text-[10px] text-[#5E6D66]">{org?.raison_sociale || acc.organization_id} — {acc.name}</p></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] ${acc.is_active ? 'border-emerald-300 text-emerald-600' : 'border-red-300 text-red-500'}`}>{acc.is_active ? 'Actif' : 'Inactif'}</Badge>
                      <Switch checked={acc.is_active} onCheckedChange={() => handleToggleClientAccount(acc)} className="scale-75" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Notifications */}
      <Card className="border-[#E8E6E1]" data-testid="client-notifications-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}><EnvelopeSimple size={18} className="text-[#FF8E00]" /> Notifications Client</CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#5E6D66]">{clientNotifSettings?.enabled ? 'Actif' : 'Inactif'}</span>
              <Switch checked={clientNotifSettings?.enabled || false} onCheckedChange={(v) => handleToggleClientNotif('enabled', v)} data-testid="client-notif-enabled-toggle" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-[#5E6D66]">Envoi automatique d'emails aux organisations quand un evenement les concerne</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#F9F8F5]" data-testid="notif-toggle-document">
              <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-[#00AEEF]/10 flex items-center justify-center"><FileText size={16} className="text-[#00AEEF]" /></div><div><p className="text-xs font-medium text-[#1A2421]">Nouveau document ajoute</p><p className="text-[10px] text-[#5E6D66]">Email envoye quand un recu, devis ou attestation est uploade</p></div></div>
              <Switch checked={clientNotifSettings?.document_added || false} onCheckedChange={(v) => handleToggleClientNotif('document_added', v)} />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#F9F8F5]" data-testid="notif-toggle-payment">
              <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-[#2D5A4C]/10 flex items-center justify-center"><CurrencyDollar size={16} className="text-[#2D5A4C]" /></div><div><p className="text-xs font-medium text-[#1A2421]">Paiement confirme</p><p className="text-[10px] text-[#5E6D66]">Email envoye quand un paiement est encaisse (manuel ou en ligne)</p></div></div>
              <Switch checked={clientNotifSettings?.payment_confirmed || false} onCheckedChange={(v) => handleToggleClientNotif('payment_confirmed', v)} />
            </div>
          </div>
          {clientNotifLogs.length > 0 && (
            <div className="pt-3 border-t border-[#E8E6E1]">
              <p className="text-xs font-semibold text-[#1A2421] uppercase mb-2">Derniers envois</p>
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto" data-testid="client-notif-logs">
                {clientNotifLogs.map((log) => (
                  <div key={log.id} className="flex items-center gap-2 p-2 rounded bg-[#FAFAF8] text-xs">
                    <Badge className={`text-[9px] ${log.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{log.success ? 'OK' : 'Echec'}</Badge>
                    <Badge variant="outline" className="text-[9px]">{log.type === 'document_added' ? 'Document' : 'Paiement'}</Badge>
                    <span className="text-[#5E6D66] truncate max-w-[120px]">{log.organization_name}</span>
                    <span className="text-[#5E6D66] font-mono">{log.to_email}</span>
                    <span className="ml-auto text-[10px] text-[#8DB0A1]">{log.sent_at ? new Date(log.sent_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Client Account Dialog */}
      <Dialog open={clientDialog} onOpenChange={setClientDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-client-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Nouveau compte client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Organisation</Label><Select value={clientForm.organization_id} onValueChange={(v) => setClientForm(p => ({ ...p, organization_id: v }))}><SelectTrigger className="text-xs" data-testid="client-form-org"><SelectValue placeholder="Choisir..." /></SelectTrigger><SelectContent>{organizations.map((o) => (<SelectItem key={o.id} value={o.id} className="text-xs">{o.raison_sociale}</SelectItem>))}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={clientForm.email} onChange={(e) => setClientForm(p => ({ ...p, email: e.target.value }))} placeholder="contact@entreprise.com" className="text-xs" data-testid="client-form-email" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Mot de passe</Label><Input type="password" value={clientForm.password} onChange={(e) => setClientForm(p => ({ ...p, password: e.target.value }))} placeholder="Mot de passe" className="text-xs" data-testid="client-form-password" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Nom (optionnel)</Label><Input value={clientForm.name} onChange={(e) => setClientForm(p => ({ ...p, name: e.target.value }))} placeholder="Nom du contact" className="text-xs" data-testid="client-form-name" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateClientAccount} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="client-form-submit">Creer le compte</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
